import "core-js/modules/es.symbol.description.js";
import "core-js/modules/web.dom-collections.iterator.js";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { LinkButton, StandardButton } from '@eika/button';
import { IconTypes } from '@eika/icon';
import { YesNoAnswer } from '../../generated-types';
import { FetchingState } from '../../reducers/pepRca';
import StandaloneYesNoQuestionType from '../../types/standalone-yes-no-question-type';
import PepHelpModal from '../../util/helpModals/pep-help-modal';
import { focusOnErrors, requiredField } from '../../util/validator';
import OverlayLoader from '../overlay-loader';
import PepItem from './pep-item';
import RcaItem from './rca-item';
const DELETED_ITEM_ID = 'DELETED';
const PepSection = _ref => {
  let {
    onSubmit,
    doPostPepRca,
    getPepRoleCountries,
    getRcaRoles,
    getPepRoles,
    countries,
    pepRoles,
    rcaRoles,
    fetchingPepRolesState,
    fetchingRcaRolesState,
    input,
    submitted,
    error,
    minCommentLength
  } = _ref;
  const [loading, setLoading] = useState(false);
  const [pepItems, setPepItems] = useState([]);
  const [lastUsedPepIndex, setLastUsedPepIndex] = useState(0);
  const [rcaItems, setRcaItems] = useState([]);
  const [lastUsedRcaIndex, setLastUsedRcaIndex] = useState(0);
  const {
    pepList,
    rcaList,
    showPep,
    customRCALabel
  } = input || [];
  const pepLabels = {
    areYouPep: 'Er du en politisk eksponert person (PEP)? En politisk eksponert person er en person som innehar, eller har innehatt, høytstående stilling eller verv.',
    isRcaPep: customRCALabel || 'Er du et nært familiemedlem eller en kjent medarbeider til en politisk eksponert person (RCA)?'
  };
  const prefillPep = pepList && pepList.length > 0;
  const prefillRca = rcaList && rcaList.length > 0;
  useEffect(() => {
    if (countries.length === 0) {
      getPepRoleCountries();
    }
  }, [getPepRoleCountries, countries]);
  useEffect(() => {
    if (fetchingPepRolesState === FetchingState.INITIAL) {
      getPepRoles();
    }
    if (fetchingRcaRolesState === FetchingState.INITIAL) {
      getRcaRoles();
    }
  }, [getPepRoles, getRcaRoles, fetchingPepRolesState, fetchingRcaRolesState]);
  const addPepItem = useCallback(() => {
    const id = "".concat(Math.random()).slice(2).padEnd(20, '0');
    const newIndex = lastUsedPepIndex + 1;
    setLastUsedPepIndex(newIndex);
    const newItem = {
      id,
      index: newIndex
    };
    setPepItems([...pepItems, newItem]);
  }, [pepItems, lastUsedPepIndex]);
  const addRcaItem = useCallback(() => {
    const id = "".concat(Math.random()).slice(2).padEnd(20, '0');
    const newIndex = lastUsedRcaIndex + 1;
    setLastUsedRcaIndex(newIndex);
    const newItem = {
      id,
      index: newIndex
    };
    setRcaItems([...rcaItems, newItem]);
  }, [rcaItems, lastUsedRcaIndex]);
  const getPepValues = input => {
    let numberOfPepItems = 0;
    const newPepItems = [];
    const pepValues = input.pepList.map(pepItem => {
      var _pepItem$roleEnd, _pepItem$roleEnd2;
      const id = "".concat(Math.random()).slice(2).padEnd(20, '0');
      const newIndex = numberOfPepItems + 1;
      const newItem = {
        id,
        index: newIndex
      };
      newPepItems.push(newItem);
      numberOfPepItems += 1;
      return {
        isFromServer: true,
        position: pepItem.roleTypeCode,
        description: pepItem.description,
        country: pepItem.roleCountry.iso3,
        month: (_pepItem$roleEnd = pepItem.roleEnd) === null || _pepItem$roleEnd === void 0 ? void 0 : _pepItem$roleEnd.month,
        year: (_pepItem$roleEnd2 = pepItem.roleEnd) === null || _pepItem$roleEnd2 === void 0 ? void 0 : _pepItem$roleEnd2.year
      };
    });
    setLastUsedPepIndex(numberOfPepItems);
    setPepItems(newPepItems);
    return pepValues;
  };
  const getRcaValues = input => {
    let numberOfRcaItems = 0;
    const newRcaItems = [];
    const rcaValues = input.rcaList.map(rcaItem => {
      var _rcaItem$roleEnd, _rcaItem$roleEnd2;
      const id = "".concat(Math.random()).slice(2).padEnd(20, '0');
      const newIndex = numberOfRcaItems + 1;
      const newItem = {
        id,
        index: newIndex
      };
      newRcaItems.push(newItem);
      numberOfRcaItems += 1;
      return {
        isFromServer: true,
        position: rcaItem.roleTypeCode,
        country: rcaItem.roleCountry.iso3,
        countryOfResidence: rcaItem.countryOfResidence.iso3,
        description: rcaItem.description,
        relationTypeCode: rcaItem.relationTypeCode,
        firstName: rcaItem.firstName,
        lastName: rcaItem.lastName,
        month: (_rcaItem$roleEnd = rcaItem.roleEnd) === null || _rcaItem$roleEnd === void 0 ? void 0 : _rcaItem$roleEnd.month,
        year: (_rcaItem$roleEnd2 = rcaItem.roleEnd) === null || _rcaItem$roleEnd2 === void 0 ? void 0 : _rcaItem$roleEnd2.year
      };
    });
    setLastUsedRcaIndex(numberOfRcaItems);
    setRcaItems(newRcaItems);
    return rcaValues;
  };
  const getInitialValues = () => {
    if (input.pepList && input.pepList.length > 0 || input.rcaList && input.rcaList.length > 0) {
      const values = {
        pepValues: getPepValues(input),
        rcaValues: getRcaValues(input),
        isPEP: YesNoAnswer.YES,
        isFromServer: true
      };
      if (values.pepValues.length > 0) {
        values.areYouPEP = YesNoAnswer.YES;
        values.areYouPEPFromServer = true;
      }
      if (values.rcaValues.length > 0) {
        values.hasRCAs = YesNoAnswer.YES;
        values.hasRCAsFromServer = true;
      }
      return values;
    }
    return undefined;
  };
  const initialValues = useMemo(getInitialValues, [input]);
  useEffect(() => {
    if (pepItems.length === 0 && !prefillPep) {
      addPepItem();
    }
    if (rcaItems.length === 0 && !prefillRca) {
      addRcaItem();
    }
  }, [addPepItem, addRcaItem, pepItems.length, rcaItems.length, prefillPep, prefillRca]);
  const onRemovePep = (id, setValueOfField) => {
    const indexToRemove = pepItems.findIndex(item => item.id === id);
    if (indexToRemove >= 0) {
      const newItems = [...pepItems];
      newItems[indexToRemove].id = DELETED_ITEM_ID;
      setPepItems(newItems);
      if (setValueOfField) {
        setValueOfField({
          key: "pep-".concat(indexToRemove),
          value: undefined
        });
      }
    }
  };
  const onRemoveRca = (id, setValueOfField) => {
    const indexToRemove = rcaItems.findIndex(item => item.id === id);
    if (indexToRemove >= 0) {
      const newItems = [...rcaItems];
      newItems[indexToRemove].id = DELETED_ITEM_ID;
      setPepItems(newItems);
      if (setValueOfField) {
        setValueOfField({
          key: "rca-".concat(indexToRemove),
          value: undefined
        });
      }
    }
  };
  const positions = [{
    text: 'Velg',
    value: ''
  }, ...pepRoles.map(el => {
    return {
      text: el.description,
      value: el.code.toString()
    };
  })];
  const getPepItems = (pepValues, setValueOfField) => {
    return pepItems.map((item, i) => {
      if (item.id !== DELETED_ITEM_ID) {
        var _pepValues$i;
        return /*#__PURE__*/React.createElement("div", {
          key: item.index
        }, /*#__PURE__*/React.createElement(PepItem, {
          countries: countries,
          positions: positions,
          index: i,
          handleRemove: () => onRemovePep(item.id, setValueOfField),
          isFromServer: pepValues && ((_pepValues$i = pepValues[i]) === null || _pepValues$i === void 0 ? void 0 : _pepValues$i.isFromServer),
          minCommentLength: minCommentLength
        }));
      }
      return undefined;
    });
  };
  const getRcaItems = (rcaValues, setValueOfField) => {
    return rcaItems.map((item, i) => {
      if (item.id !== DELETED_ITEM_ID) {
        var _rcaValues$i;
        return /*#__PURE__*/React.createElement("div", {
          key: item.index
        }, /*#__PURE__*/React.createElement(RcaItem, {
          countries: countries,
          rcaRoles: rcaRoles,
          positions: positions,
          index: i,
          handleRemove: () => onRemoveRca(item.id, setValueOfField),
          isFromServer: rcaValues && ((_rcaValues$i = rcaValues[i]) === null || _rcaValues$i === void 0 ? void 0 : _rcaValues$i.isFromServer),
          minCommentLength: minCommentLength
        }));
      }
      return undefined;
    });
  };
  const getRoleCountry = countryIso3 => {
    return countries.find(country => country.iso3 === countryIso3);
  };
  const createPepListToSend = formValues => {
    const pepListToSend = [];
    for (let i = 0; i < pepItems.length; i += 1) {
      if (pepItems[i].id !== DELETED_ITEM_ID) {
        const roleTypeCode = formValues.pepValues[i].position;
        const pepList = {
          roleTypeCode,
          roleCountry: getRoleCountry(formValues.pepValues[i].country),
          description: formValues.pepValues[i].description
        };
        if (formValues.pepValues[i].month && formValues.pepValues[i].year) {
          pepList.roleEnd = {
            month: formValues.pepValues[i].month,
            year: formValues.pepValues[i].year
          };
        }
        pepListToSend.push(pepList);
      }
    }
    return pepListToSend;
  };
  const createRcaListToSend = formValues => {
    const rcaListToSend = [];
    for (let i = 0; i < rcaItems.length; i += 1) {
      if (rcaItems[i].id !== DELETED_ITEM_ID) {
        const rcaTypeCode = formValues.rcaValues[i].position;
        const {
          description
        } = formValues.rcaValues[i];
        const {
          relationTypeCode
        } = formValues.rcaValues[i];
        const rca = {
          roleTypeCode: rcaTypeCode,
          roleCountry: getRoleCountry(formValues.rcaValues[i].country),
          description,
          countryOfResidence: getRoleCountry(formValues.rcaValues[i].countryOfResidence),
          relationTypeCode,
          firstName: formValues.rcaValues[i].firstName,
          lastName: formValues.rcaValues[i].lastName
        };
        if (formValues.rcaValues[i].month && formValues.rcaValues[i].year) {
          rca.roleEnd = {
            month: formValues.rcaValues[i].month,
            year: formValues.rcaValues[i].year
          };
        }
        rcaListToSend.push(rca);
      }
    }
    return rcaListToSend;
  };
  const createPutFromFormValues = formValues => {
    const objectToSend = {
      pepList: formValues.areYouPEP === 'YES' ? createPepListToSend(formValues) : [],
      rcaList: formValues.hasRCAs === 'YES' ? createRcaListToSend(formValues) : []
    };
    return objectToSend;
  };
  const getAreYouPepYesNo = values => {
    const {
      areYouPEP
    } = values;
    if (values.areYouPEPFromServer && areYouPEP === YesNoAnswer.YES) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
        className: "disabled-yes-no-question"
      }, pepLabels.areYouPep), /*#__PURE__*/React.createElement("span", {
        className: "small-margin-left"
      }, "Ja"));
    }
    return /*#__PURE__*/React.createElement(StandaloneYesNoQuestionType, {
      selected: areYouPEP,
      label: pepLabels.areYouPep,
      name: "areYouPEP",
      validate: requiredField
    });
  };
  const getIsRcaPepYesNo = values => {
    const {
      hasRCAs
    } = values;
    if (values.isRcaPepFromServer && hasRCAs === YesNoAnswer.YES) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
        className: "disabled-yes-no-question"
      }, pepLabels.isRcaPep), /*#__PURE__*/React.createElement("span", {
        className: "small-margin-left"
      }, "Ja"));
    }
    return /*#__PURE__*/React.createElement(StandaloneYesNoQuestionType, {
      selected: hasRCAs,
      label: pepLabels.isRcaPep,
      name: "hasRCAs",
      validate: requiredField
    });
  };
  const addPepRca = data => {
    setLoading(true);
    doPostPepRca(createPutFromFormValues(data));
  };
  useEffect(() => {
    if (submitted) {
      setLoading(false);
      onSubmit();
    }
  }, [submitted, onSubmit]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, loading && !error ? /*#__PURE__*/React.createElement(OverlayLoader, null) : undefined, /*#__PURE__*/React.createElement("h2", {
    className: "header-title"
  }, "Politisk eksponert person", /*#__PURE__*/React.createElement(PepHelpModal, null)), /*#__PURE__*/React.createElement(Form, {
    onSubmit: addPepRca,
    decorators: [focusOnErrors],
    formName: "pep-rca-form",
    initialValues: initialValues,
    mutators: {
      setValueOfField: (args, state, utils) => {
        utils.changeValue(state, args[0].key, () => args[0].value);
      }
    }
  }, props => {
    return /*#__PURE__*/React.createElement("form", {
      onSubmit: props.handleSubmit
    }, (showPep || props.values.isPEP === YesNoAnswer.YES) && /*#__PURE__*/React.createElement("div", {
      className: "form-question"
    }, getAreYouPepYesNo(props.values), (props.values.areYouPEP === YesNoAnswer.YES || prefillPep) && getPepItems(props.values.pepValues, props.form.mutators.setValueOfField), props.values.areYouPEP === YesNoAnswer.YES && /*#__PURE__*/React.createElement(LinkButton, {
      id: "add-more-pep-roles",
      icon: IconTypes.PLUSS_24,
      onClick: addPepItem
    }, "Legg til")), /*#__PURE__*/React.createElement("div", {
      className: "form-question"
    }, getIsRcaPepYesNo(props.values), (props.values.hasRCAs === YesNoAnswer.YES || prefillRca) && getRcaItems(props.values.rcaValues, props.form.mutators.setValueOfField), props.values.hasRCAs === YesNoAnswer.YES && /*#__PURE__*/React.createElement(LinkButton, {
      id: "add-more-rca-roles",
      icon: IconTypes.PLUSS_24,
      onClick: addRcaItem
    }, "Legg til")), /*#__PURE__*/React.createElement(StandardButton, {
      className: "progress-next-button",
      type: "submit"
    }, "Neste"));
  }));
};
export default PepSection;